import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from "@angular/core"
import { Router } from "@angular/router"
import { SKETCH_URL } from "../../app-urls"
import { CURRENT_STATE_PROJECT_KEY, LinqsetNode, MODEL_TYPE, ORGANISATION_MODEL_KEY } from "../../common/linqset-model"
import { Organisation } from "../../common/organisation"
import { getUserMessage } from "../../common/exceptions"
import { ORGANISATION_API, RepositoryService } from "../../linq-utils/repository.service"
import { Column } from "../../ui-components/table/table-component"

const COMPONENT = "To do list"

// what the table shows for a todo; opening it looks the todo up again by _key
interface TodoRow {
    _key:string
    created:Date
    name:string
    description:string
    notes:string
    model:string
    workspace:string
    canOpen:boolean
}

@Component({
    selector: 'app-todo-list',
    templateUrl: './todo-list.html',
    styleUrls: ['./todo-list.css'],
    standalone: false
})
export class TodoListComponent implements OnChanges{
    @Input() organisation:Organisation
    @Output() countChanged = new EventEmitter<number>()

    todos:LinqsetNode[] = []
    data:TodoRow[] = []
    loading = false
    loaded = false
    error:string|undefined

    columns = [
        new Column('name', 'To do', 'string'),
        new Column('description', 'Description', 'string'),
        new Column('notes', 'Notes', 'string'),
        new Column('model', 'Model', 'string'),
        new Column('workspace', 'In', 'string'),
        new Column('created', 'Added', 'date'),
        new Column('canOpen', 'Open', 'icon-button', 'Open the model at this item', 'accent', 'open_in_new'),
    ]

    constructor(private repoService:RepositoryService, private router:Router) {
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['organisation'] && this.organisation) this.loadData()
    }

    async loadData() {
        this.loading = true
        this.error = undefined
        try {
            // loaded in the background on the home page, so the list shows any problem rather than a dialog
            this.todos = await this.repoService.callSingleAPI(this.repoService.getData(ORGANISATION_API(this.organisation._key) + "/notes"), COMPONENT, "load data", undefined, this.organisation, undefined, undefined, false) || []
            this.data = this.todos
                .map(t => {
                    const model = t.modelKey === ORGANISATION_MODEL_KEY ? this.organisation.model : this.organisation.models.find(m => m._key === t.modelKey)
                    return {
                        _key: t._key,
                        created: t.createdDate,
                        name: t.name || 'Untitled',
                        description: t.description,
                        notes: t.notes,
                        model: model?.name || '',
                        workspace: this.workspaceName(t.projectKey),
                        canOpen: !!model?.role,
                    }
                })
                // newest first
                .sort((a, b) => new Date(b.created || 0).getTime() - new Date(a.created || 0).getTime())
            this.countChanged.emit(this.data.length)
        }catch(err) {
            this.error = getUserMessage(err)
        }finally {
            this.loading = false
            this.loaded = true
        }
    }

    private workspaceName(projectKey:string) {
        if (!projectKey || projectKey === CURRENT_STATE_PROJECT_KEY) return 'Current state'
        return this.organisation.projects.find(p => p._key === projectKey)?.name || ''
    }

    async handleRowEvent(rowEvent:{row:TodoRow, column?:Column}) {
        if (rowEvent.row?.canOpen) this.openTodo(rowEvent.row)
    }

    // opens the model with the todo selected
    openTodo(row:TodoRow) {
        const todo = this.todos.find(t => t._key === row._key)
        if (!todo) return
        this.router.navigateByUrl(SKETCH_URL(this.organisation._key, todo.modelKey, MODEL_TYPE.PROCESS, todo.projectKey || CURRENT_STATE_PROJECT_KEY) + "?nodeKey=" + todo._key)
    }
}
